// src/design/registry/sectionRegistry.ts
// Kiosk check-in section (S,R,I,F,U,B,P,K) -> { Icon, color token, labelKey }.
// Reuses the existing activityMonitor.sections i18n keys.
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import BadgeIcon from '@mui/icons-material/Badge';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import type { VisualMeta } from './colors';

// R = Room Selection and B = Bill summary are emitted only in the walk-in flow
// (guests with no reservation). B is the screen showing the amount due, just before payment.
export type SectionCode = 'S' | 'R' | 'I' | 'F' | 'U' | 'B' | 'P' | 'K';

/** Display order of the kiosk check-in funnel. */
export const SECTION_ORDER: SectionCode[] = ['S', 'R', 'I', 'F', 'U', 'B', 'P', 'K'];

export const SECTION_REGISTRY: Record<SectionCode, VisualMeta> = {
  S: { Icon: PlayArrowIcon,   color: 'info',      labelKey: 'resources.activityMonitor.sections.s' },
  R: { Icon: MeetingRoomIcon, color: 'secondary', labelKey: 'resources.activityMonitor.sections.r' },
  I: { Icon: BadgeIcon,       color: 'secondary', labelKey: 'resources.activityMonitor.sections.i' },
  F: { Icon: EditIcon,        color: 'warning',   labelKey: 'resources.activityMonitor.sections.f' },
  U: { Icon: AddIcon,         color: 'info',      labelKey: 'resources.activityMonitor.sections.u' },
  B: { Icon: ReceiptLongIcon, color: 'info',      labelKey: 'resources.activityMonitor.sections.b' },
  P: { Icon: PaymentIcon,     color: 'success',   labelKey: 'resources.activityMonitor.sections.p' },
  K: { Icon: VpnKeyIcon,      color: 'primary',   labelKey: 'resources.activityMonitor.sections.k' },
};

export const getSectionMeta = (sectionCode: string | null | undefined): VisualMeta | undefined => {
  if (!sectionCode) return undefined;
  return SECTION_REGISTRY[sectionCode as SectionCode]
    ?? { Icon: HelpOutlineIcon, color: 'neutral', labelKey: sectionCode };
};

/** Human name for a section code. Never returns a bare S/R/I/F/U/B/P/K (proposal §3). */
export const getSectionLabel = (
  sectionCode: string | null | undefined,
  translate: (key: string) => string
): string => {
  const meta = getSectionMeta(sectionCode);
  return meta ? translate(meta.labelKey) : (sectionCode ?? '');
};
